/* ---- OpenLayers Map component ---- */

import Feature from 'ol/Feature';
import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import View from 'ol/View';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style, Text } from 'ol/style';
import type { Geofence } from '../types';

import 'ol/ol.css';

export interface MapViewOptions {
  target: HTMLElement;
  center?: [number, number]; // [lng, lat]
  zoom?: number;
}

export class MapViewComponent {
  private map: Map;
  private view: View;
  private markerSource: VectorSource;
  private geofenceSource: VectorSource;
  private pulseOverlay: Overlay | null = null;
  private markerOverlay: Overlay | null = null;

  constructor(opts: MapViewOptions) {
    const center = opts.center || [139.7638, 35.6862];
    const zoom = opts.zoom || 16;

    this.markerSource = new VectorSource();
    this.geofenceSource = new VectorSource();

    this.view = new View({
      center: fromLonLat(center),
      zoom,
    });

    this.map = new Map({
      target: opts.target,
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({
          source: this.geofenceSource,
          style: (feature) => {
            const inSchedule = feature.get('inSchedule') !== false;
            // Phase 4: 時間外 (inSchedule=false) は色設定によらず薄いグレーで塗りつぶす
            const GRAY = '#9CA3AF';
            const color = inSchedule ? (feature.get('color') as string) || '#0066FF' : GRAY;
            const rawName = (feature.get('name') as string) || '';
            const name = inSchedule ? rawName : rawName ? `${rawName} (時間外)` : '';
            return new Style({
              fill: new Fill({ color: this.hexToRgba(color, inSchedule ? 0.15 : 0.25) }),
              stroke: new Stroke({
                color: this.hexToRgba(color, inSchedule ? 0.6 : 0.5),
                width: 2,
              }),
              text: name
                ? new Text({
                    text: name,
                    font: 'bold 11px sans-serif',
                    fill: new Fill({ color: inSchedule ? color : '#6B7280' }),
                    stroke: new Stroke({ color: '#fff', width: 3 }),
                    overflow: true,
                  })
                : undefined,
            });
          },
        }),
        new VectorLayer({
          source: this.markerSource,
          style: new Style({
            image: new Icon({
              src:
                'data:image/svg+xml;charset=utf-8,' +
                encodeURIComponent(
                  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><circle cx="16" cy="16" r="10" fill="#0066FF" stroke="#fff" stroke-width="3"/></svg>',
                ),
              scale: 0.5,
              anchor: [0.5, 0.5],
            }),
          }),
        }),
      ],
      view: this.view,
      controls: [],
    });

    // Force map to resize correctly
    setTimeout(() => this.map.updateSize(), 100);
  }

  /** Set current location marker with pulse animation */
  setCurrentLocation(lng: number, lat: number): void {
    this.markerSource.clear();

    const point = new Point(fromLonLat([lng, lat]));
    const feature = new Feature({ geometry: point });
    this.markerSource.addFeature(feature);

    // Pulse overlay
    if (this.pulseOverlay) {
      this.map.removeOverlay(this.pulseOverlay);
    }
    if (this.markerOverlay) {
      this.map.removeOverlay(this.markerOverlay);
    }

    const pulseEl = document.createElement('div');
    pulseEl.className = 'current-location-pulse';
    this.pulseOverlay = new Overlay({
      element: pulseEl,
      position: fromLonLat([lng, lat]),
      positioning: 'center-center',
    });
    this.map.addOverlay(this.pulseOverlay);

    const markerEl = document.createElement('div');
    markerEl.className = 'current-location-marker';
    this.markerOverlay = new Overlay({
      element: markerEl,
      position: fromLonLat([lng, lat]),
      positioning: 'center-center',
    });
    this.map.addOverlay(this.markerOverlay);
  }

  /** Set a static pin at a position (for event detail) */
  setPin(lng: number, lat: number): void {
    this.markerSource.clear();
    const feature = new Feature({ geometry: new Point(fromLonLat([lng, lat])) });
    this.markerSource.addFeature(feature);
  }

  /** Draw geofence polygons on the map */
  setGeofences(geofences: Geofence[]): void {
    this.geofenceSource.clear();
    geofences.forEach((gf) => {
      if (gf.coordinates && gf.coordinates.length >= 3) {
        const coords = gf.coordinates.map((c) => fromLonLat(c));
        const polygon = new Polygon([coords]);
        const feature = new Feature({ geometry: polygon });
        feature.set('color', gf.color || '#0066FF');
        feature.set('name', gf.name);
        feature.set('inSchedule', gf.inSchedule !== false);
        this.geofenceSource.addFeature(feature);
      }
    });
  }

  /** Center the map on given coordinates */
  centerOn(lng: number, lat: number, zoom?: number): void {
    this.view.animate({
      center: fromLonLat([lng, lat]),
      zoom: zoom || this.view.getZoom(),
      duration: 300,
    });
  }

  zoomIn(): void {
    const z = this.view.getZoom();
    if (z !== undefined) this.view.animate({ zoom: z + 1, duration: 200 });
  }

  zoomOut(): void {
    const z = this.view.getZoom();
    if (z !== undefined) this.view.animate({ zoom: z - 1, duration: 200 });
  }

  updateSize(): void {
    this.map.updateSize();
  }

  destroy(): void {
    this.map.setTarget(undefined);
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
